/**
 * Sales Report Helper Functions
 * Utilities for date conversion, data processing, and aggregation
 */

/**
 * Convert a JavaScript Date to Firestore collection name format
 * Assuming collection names are Julian day numbers or similar format
 * @param {Date|string} date - The date to convert
 * @returns {string} Collection name
 */
export function convertDateToCollectionName(date) {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Calculate days since epoch (Jan 1, 1970)
  const daysSinceEpoch = Math.floor(dateObj.getTime() / (1000 * 60 * 60 * 24));
  
  return daysSinceEpoch.toString();
}

/**
 * Get all collection names for a given month and year
 * @param {string} month - Month in format "01", "02", etc.
 * @param {string|number} year - Year (e.g., 2024)
 * @returns {string[]} Array of collection names
 */
export function getCollectionNamesForMonth(month, year) {
  const monthNum = parseInt(month) - 1; // JS months are 0-indexed
  const yearNum = parseInt(year);
  
  // Get first day of month
  const startDate = new Date(yearNum, monthNum, 1);
  
  // Get last day of month
  const endDate = new Date(yearNum, monthNum + 1, 0);
  
  const collectionNames = [];
  
  // Iterate through each day of the month
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    collectionNames.push(convertDateToCollectionName(new Date(d)));
  }
  
  return collectionNames;
}

/**
 * Parse month-year string to month and year
 * @param {string} monthYear - Format: "01-2024" or "MM-YYYY"
 * @returns {{month: string, year: string}}
 */
export function parseMonthYear(monthYear) {
  const [month, year] = monthYear.split('-');
  return { month, year };
}

/**
 * Calculate total amount from products array
 * @param {Array} products - Array of product objects
 * @returns {number} Total amount
 */
export function calculateTotal(products) {
  if (!products || !Array.isArray(products)) return 0;
  
  return products.reduce((total, product) => {
    const price = parseFloat(product.price || 0);
    const quantity = parseFloat(product.quantity || 1);
    return total + (price * quantity);
  }, 0);
}

/**
 * Extract unique categories from products Redux state
 * @param {Array} products - Products from Redux state
 * @returns {string[]} Unique categories
 */
export function extractCategoriesFromProducts(products) {
  if (!products || !Array.isArray(products)) return [];
  
  const categories = new Set();
  
  products.forEach(product => {
    if (product.category_english) {
      categories.add(product.category_english);
    }
  });
  
  return Array.from(categories).sort();
}

/**
 * Extract unique types from products Redux state
 * @param {Array} products - Products from Redux state
 * @returns {string[]} Unique types
 */
export function extractTypesFromProducts(products) {
  if (!products || !Array.isArray(products)) return [];
  
  const types = new Set();
  
  products.forEach(product => {
    if (product.type) {
      types.add(product.type);
    }
  });
  
  return Array.from(types).sort();
}

/**
 * Process raw Firestore orders into report data format
 * @param {Array} orders - Raw Firestore order documents
 * @param {Array} productsRedux - Products from Redux for enrichment
 * @param {Array} customersRedux - Customers from Redux for enrichment
 * @returns {Array} Processed report data
 */
export function processReportData(orders, productsRedux = [], customersRedux = []) {
  if (!orders || !Array.isArray(orders)) return [];
  
  return orders.map(order => {
    const orderData = order.order || {};
    const products = orderData.products || [];
    
    // customer_id is now at the root level of the order (added during fetch)
    const customerId = order.customer_id || orderData.customer_id;
    
    // Find customer info
    const customer = customersRedux.find(c => c.id === customerId) || {};
    
    // Calculate total
    const total = calculateTotal(products);
    
    // Enrich products with full product info
    const enrichedProducts = products.map(prod => {
      const fullProduct = productsRedux.find(p => p.id === prod.id || p.id === prod.product_id) || {};
      return {
        ...prod,
        ...fullProduct,
        subtotal: (parseFloat(prod.price || 0) * parseFloat(prod.quantity || 1))
      };
    });
    
    return {
      orderId: order.id,
      customerId: customerId,
      customerName: customer.name_english || orderData.customer_name || 'Unknown',
      customerNameUrdu: customer.name_urdu || '',
      category: orderData.category,
      type: orderData.type || 'current',
      deliveredDate: order.delivered?.toDate ? order.delivered.toDate() : order.delivered,
      modificationDate: order.modification?.toDate ? order.modification.toDate() : order.modification,
      products: enrichedProducts,
      totalAmount: total,
      rawData: {
        ...order,
        data: order.data || {} // Ensure data object is included
      }
    };
  });
}

/**
 * Aggregate report data by category
 * @param {Array} reportData - Processed report data
 * @returns {Object} Aggregated data by category
 */
export function aggregateByCategory(reportData) {
  const aggregated = {};
  
  reportData.forEach(order => {
    const category = order.category || 'Uncategorized';
    
    if (!aggregated[category]) {
      aggregated[category] = {
        category,
        totalOrders: 0,
        totalAmount: 0,
        orders: []
      };
    }
    
    aggregated[category].totalOrders++;
    aggregated[category].totalAmount += order.totalAmount;
    aggregated[category].orders.push(order);
  });
  
  return aggregated;
}

/**
 * Aggregate report data by customer
 * @param {Array} reportData - Processed report data
 * @returns {Object} Aggregated data by customer
 */
export function aggregateByCustomer(reportData) {
  const aggregated = {};
  
  reportData.forEach(order => {
    const customerId = order.customerId || 'unknown';
    
    if (!aggregated[customerId]) {
      aggregated[customerId] = {
        customerId,
        customerName: order.customerName,
        customerNameUrdu: order.customerNameUrdu,
        totalOrders: 0,
        totalAmount: 0,
        orders: []
      };
    }
    
    aggregated[customerId].totalOrders++;
    aggregated[customerId].totalAmount += order.totalAmount;
    aggregated[customerId].orders.push(order);
  });
  
  return aggregated;
}

/**
 * Aggregate report data by product
 * @param {Array} reportData - Processed report data
 * @returns {Array} Aggregated data by product
 */
export function aggregateByProduct(reportData) {
  const productMap = {};
  
  reportData.forEach(order => {
    order.products.forEach(product => {
      const productId = product.id || product.product_id;
      
      if (!productMap[productId]) {
        productMap[productId] = {
          productId,
          productName: product.name_english || product.name || 'Unknown',
          productNameUrdu: product.name_urdu || '',
          category: product.category_english || product.category,
          totalQuantity: 0,
          totalAmount: 0,
          orderCount: 0
        };
      }
      
      productMap[productId].totalQuantity += parseFloat(product.quantity || 0);
      productMap[productId].totalAmount += product.subtotal || 0;
      productMap[productId].orderCount++;
    });
  });
  
  return Object.values(productMap).sort((a, b) => b.totalAmount - a.totalAmount);
}

/**
 * Format date to display string
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0
  }).format(amount || 0);
}

/**
 * Generate years array for dropdown
 * @param {number} yearsBack - How many years back from current year
 * @returns {string[]} Array of years
 */
export function generateYears(yearsBack = 5) {
  const currentYear = new Date().getFullYear();
  const years = [];
  
  for (let i = 0; i <= yearsBack; i++) {
    years.push((currentYear - i).toString());
  }
  
  return years;
}

